import {
  firstSentences,
  group,
  groupsCss,
  hero,
  heroCss,
  kitPartCss,
  microHeading,
  microHeadingCss,
  pageLabel,
  panel,
  panelCss,
  positive,
} from '../armKit';
import { deepWorkFacts } from '../pageData';
import { WARM_HAIRLINE } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';

type PickKind = 'most-read' | 'deepest' | 'latest';

type Candidate = WorkFacts & { workId: number; title: string; url: string };

type Highlight = { kind: PickKind; label: string; candidate: Candidate };

const TITLE = 'Start here';
const SOURCE = 'profile-liautotest-highlights';

// Soft claims only, on every label below: each pick ranks a capped fetch, so
// a superlative — "most read", "deepest", "latest" — would overclaim on
// exactly the corpora the cap trims. The pick kinds keep their superlative
// names; those are analytics keys, not copy.
const MOST_READ_LABEL = 'Widely read';

function armCss(scope: string): string {
  return (
    panelCss(scope, { frame: 'warm', marginBottom: '0' }) +
    kitPartCss('panelTitle', scope, 'font-size: 16px; margin: 0 0 12px;') +
    microHeadingCss(scope) +
    groupsCss(scope, { gap: '16px', rule: '1px solid ' + WARM_HAIRLINE }) +
    heroCss(scope, { abstractMarginBottom: '6px' }) +
    kitPartCss('heroTitle', scope, 'font-size: 16px;') +
    kitPartCss('heroAbstract', scope, 'font-size: 13px;')
  );
}

function candidatesOf(facts: Map<number, WorkFacts>): Candidate[] {
  return [...facts].flatMap(([workId, work]) =>
    work.title != null && work.url != null
      ? [{ ...work, workId, title: work.title, url: work.url }]
      : [],
  );
}

function best(
  candidates: Candidate[],
  taken: Set<number>,
  score: (candidate: Candidate) => number | null,
): Candidate | null {
  const scored = candidates.flatMap((candidate) => {
    const value = score(candidate);

    return taken.has(candidate.workId) || value == null
      ? []
      : [{ candidate, value }];
  });

  return (
    scored.reduce<{ candidate: Candidate; value: number } | null>(
      (top, entry) => (top == null || entry.value > top.value ? entry : top),
      null,
    )?.candidate ?? null
  );
}

// Priority order: a work topping two criteria keeps the first label, and the
// later criterion falls to the next-best distinct work, so three rows are
// always three papers.
function highlightsOf(candidates: Candidate[]): Highlight[] {
  const taken = new Set<number>();

  const take = (
    kind: PickKind,
    score: (candidate: Candidate) => number | null,
    label: (candidate: Candidate) => string,
  ): Highlight[] => {
    const candidate = best(candidates, taken, score);
    if (candidate == null) return [];

    taken.add(candidate.workId);

    return [{ kind, label: label(candidate), candidate }];
  };

  return [
    ...take(
      'most-read',
      (candidate) => positive(candidate.views),
      () => MOST_READ_LABEL,
    ),
    ...take(
      'deepest',
      (candidate) => positive(candidate.pageCount),
      (candidate) => {
        const pages = pageLabel(candidate.pageCount);

        return pages == null ? 'A deep dive' : `A deep dive (${pages})`;
      },
    ),
    // The catalog endpoint's order is NOT recency, and work ids are
    // creation-ordered, so max id is the newest work the roster can vouch for.
    ...take(
      'latest',
      (candidate) => candidate.workId,
      () => 'Recent',
    ),
  ];
}

// The title and Read land on the same work page, so they share the pick's
// source; Download is one source across picks, carrying the pick in extra.
function rowOf(highlight: Highlight, ctx: ArmContext): HTMLElement {
  const { kind, label, candidate } = highlight;
  const section = group(kind);
  const trackPick = (): void => {
    ctx.track(`${SOURCE}-${kind}`, { entityId: candidate.workId });
  };

  section.appendChild(microHeading(label));

  hero(section, {
    title: candidate.title,
    href: candidate.url,
    onTitleClick: trackPick,
    abstract:
      candidate.summary == null
        ? undefined
        : firstSentences(candidate.summary, 1),
    actions: [
      { label: 'Read', href: candidate.url, onClick: trackPick },
      ...(candidate.downloadUrl == null
        ? []
        : [
            {
              label: 'Download',
              href: candidate.downloadUrl,
              onClick: () => {
                ctx.track(`${SOURCE}-download`, {
                  entityId: candidate.workId,
                  extra: { pick: kind },
                });
              },
            },
          ]),
    ],
  });

  return section;
}

export const startHereHighlights: ArmSpec = {
  onReady: async (ctx) => {
    const highlights = highlightsOf(
      candidatesOf(await deepWorkFacts(ctx.viewedUserId)),
    );
    // A lone row is an editorial pick rather than a map of the catalog, so
    // under two distinct works the arm keeps quiet.
    if (highlights.length < 2) return;

    const slot = ctx.mountSlot('columnTop');
    if (slot == null) return;

    ctx.injectStyles(armCss(ctx.scope));

    const { root, body } = panel({ title: TITLE });
    highlights.forEach((highlight) => body.appendChild(rowOf(highlight, ctx)));

    slot.appendChild(root);
    ctx.markRendered();
  },
};
